import React from 'react';
import { ReactComponent as ArrowRightIcon } from '../../../_shared/icons/arrow-right.svg';

interface PaginationProps
	{
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	}

// A gap in the run of page numbers, rendered as the design's "..." -- a
// string rather than a number so the caller can tell the two apart when
// mapping without a second lookup.
const ELLIPSIS = 'ellipsis';

type PageItem = number | typeof ELLIPSIS;

// The design (Figma node 4168:1246) shows "1 2 3 4 ... 15": a run of pages
// around the current one, an ellipsis, then the last page. Reproduced here
// rather than rendering every page, which at 45 properties / 12 per page is
// harmless but stops being so as the portfolio grows.
//
// Both ends are elided independently, so a current page in the middle gives
// "1 ... 7 8 9 ... 15". The sibling count is 1 either side, plus first/last,
// which is what keeps the row at the design's width.
export const getPageItems = (currentPage: number, totalPages: number): PageItem[] =>
	{
	// 7 is the most slots this can show without an ellipsis (first, last, the
	// current page, one sibling each side, and the two positions an ellipsis
	// would otherwise occupy) -- below that, every page fits and eliding
	// would make the row longer, not shorter.
	if (totalPages <= 7)
		{
		return Array.from({ length: totalPages }, (_, index) => index + 1);
		}

	const items: PageItem[] = [1];

	const windowStart = Math.max(2, currentPage - 1);
	const windowEnd = Math.min(totalPages - 1, currentPage + 1);

	// Only elide when doing so actually removes something. A gap of exactly
	// one page would render "1 ... 3", which is wider than "1 2 3" and hides
	// a page for no gain.
	if (windowStart > 3)
		{
		items.push(ELLIPSIS);
		}
	else if (windowStart === 3)
		{
		items.push(2);
		}

	for (let page = windowStart; page <= windowEnd; page += 1)
		{
		items.push(page);
		}

	if (windowEnd < totalPages - 2)
		{
		items.push(ELLIPSIS);
		}
	else if (windowEnd === totalPages - 2)
		{
		items.push(totalPages - 1);
		}

	items.push(totalPages);

	return items;
	};

export const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) =>
	{
	if (totalPages <= 1)
		{
		return null;
		}

	const items = getPageItems(currentPage, totalPages);

	return (
		<nav className="property-results__pagination" aria-label="Search results pages">
			<button
				type="button"
				className="property-results__pagination-arrow property-results__pagination-arrow--prev"
				onClick={() => onPageChange(currentPage - 1)}
				disabled={currentPage === 1}
				aria-label="Previous page"
			>
				<ArrowRightIcon />
			</button>

			{items.map((item, index) => (
				item === ELLIPSIS
					// Ellipses carry no page, so several can share the row and
					// the index is the only thing separating them.
					? (
						<span
							key={`ellipsis-${index}`}
							className="property-results__pagination-ellipsis"
							aria-hidden="true"
						>
							…
						</span>
						)
					: (
						<button
							key={item}
							type="button"
							className={`property-results__pagination-number ${item === currentPage ? 'is-current' : ''}`}
							onClick={() => onPageChange(item)}
							aria-label={`Page ${item}`}
							aria-current={item === currentPage ? 'page' : undefined}
						>
							{item}
						</button>
						)
			))}

			<button
				type="button"
				className="property-results__pagination-arrow property-results__pagination-arrow--next"
				onClick={() => onPageChange(currentPage + 1)}
				disabled={currentPage === totalPages}
				aria-label="Next page"
			>
				<ArrowRightIcon />
			</button>
		</nav>
		);
	};
